import { Client, type StompSubscription } from '@stomp/stompjs';
import { env } from '@shared/config/env';
import type { SubscriptionHandle, TokenProvider, WsState } from './types';

type Listener<T> = (value: T) => void;

interface PendingSubscription<T> {
  destination: string;
  handler: Listener<T>;
  active?: StompSubscription;
}

class StompClientManager {
  private client: Client | null = null;
  private state: WsState = 'idle';
  private getToken: TokenProvider = () => null;
  private subscriptions = new Map<string, PendingSubscription<unknown>>();
  private stateListeners = new Set<Listener<WsState>>();
  private subIdSeq = 0;

  connect(getToken: TokenProvider): void {
    if (this.client) return;
    this.getToken = getToken;

    const client = new Client({
      brokerURL: env.wsUrl,
      connectHeaders: this.authHeaders(),
      reconnectDelay: 1000,
      heartbeatIncoming: 10_000,
      heartbeatOutgoing: 10_000,
      beforeConnect: () => {
        client.connectHeaders = this.authHeaders();
      },
      onConnect: () => {
        this.setState('connected');
        for (const sub of this.subscriptions.values()) {
          this.bindSubscription(sub);
        }
      },
      onWebSocketClose: () => {
        for (const sub of this.subscriptions.values()) sub.active = undefined;
        this.setState(this.client ? 'reconnecting' : 'disconnected');
      },
      onStompError: (frame) => {
        console.error('STOMP error', frame.headers, frame.body);
      },
      debug: () => {
        /* silent */
      },
    });

    // exponential backoff 1s → 2s → 4s → 8s → max 30s
    let attempt = 0;
    const baseDelay = 1000;
    const maxDelay = 30_000;
    client.reconnectDelay = baseDelay;
    client.onDisconnect = () => {
      attempt = 0;
      client.reconnectDelay = baseDelay;
    };
    client.onWebSocketClose = () => {
      for (const sub of this.subscriptions.values()) sub.active = undefined;
      attempt = Math.min(attempt + 1, 8);
      client.reconnectDelay = Math.min(baseDelay * 2 ** (attempt - 1), maxDelay);
      this.setState(this.client ? 'reconnecting' : 'disconnected');
    };

    this.client = client;
    this.setState('connecting');
    client.activate();
  }

  disconnect(): void {
    if (!this.client) return;
    void this.client.deactivate();
    this.client = null;
    for (const sub of this.subscriptions.values()) sub.active = undefined;
    this.setState('disconnected');
  }

  subscribe<T>(destination: string, handler: Listener<T>): SubscriptionHandle {
    const id = `sub-${++this.subIdSeq}-${destination}`;
    const pending: PendingSubscription<T> = { destination, handler };
    this.subscriptions.set(id, pending as PendingSubscription<unknown>);
    if (this.client?.connected) {
      this.bindSubscription(pending as PendingSubscription<unknown>);
    }
    return {
      unsubscribe: () => {
        const s = this.subscriptions.get(id);
        s?.active?.unsubscribe();
        this.subscriptions.delete(id);
      },
    };
  }

  send(destination: string, body: unknown, headers: Record<string, string> = {}): boolean {
    if (!this.client?.connected) return false;
    this.client.publish({
      destination,
      body: typeof body === 'string' ? body : JSON.stringify(body),
      headers: { 'content-type': 'application/json', ...headers },
    });
    return true;
  }

  onState(listener: Listener<WsState>): () => void {
    this.stateListeners.add(listener);
    listener(this.state);
    return () => this.stateListeners.delete(listener);
  }

  getState(): WsState {
    return this.state;
  }

  private authHeaders(): Record<string, string> {
    const token = this.getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  private bindSubscription<T>(sub: PendingSubscription<T>): void {
    if (!this.client?.connected) return;
    sub.active = this.client.subscribe(sub.destination, (frame) => {
      let parsed: T;
      try {
        parsed = JSON.parse(frame.body) as T;
      } catch {
        parsed = frame.body as unknown as T;
      }
      sub.handler(parsed);
    });
  }

  private setState(next: WsState): void {
    if (this.state === next) return;
    this.state = next;
    for (const l of this.stateListeners) l(next);
  }
}

export const stompClient = new StompClientManager();
